import * as React from 'react';

import { DiagramTransaction } from 'src/containers/ProjectDiagram/constants/transactions';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { LinkData } from 'src/containers/ProjectDiagram/types/model';
import { addDeletedData } from 'src/containers/ProjectDiagram/utils/addDeletedData';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
  commitDiagramWhileSkippingUndoManager,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { isMapping } from 'src/containers/ProjectDiagram/utils/data';
import { removeMapping } from 'src/containers/ProjectDiagram/utils/mapping/removeMapping';
import { removePartsFromSelection } from 'src/containers/ProjectDiagram/utils/removePartsFromSelection';

export const useRemoveResourceMapping = () => {
  const diagram = useDiagram();

  return React.useCallback(
    (mappingKey: string) => {
      if (!diagram) {
        return;
      }

      // Resolve the mapping from the model (works headless) rather than the
      // diagram's laid-out links.
      const linkData = diagram.model.findLinkDataForKey(mappingKey) as
        | LinkData
        | null
        | undefined;

      if (!linkData || !isMapping(linkData)) {
        return;
      }

      // Removing the link/label from the selection is a canvas-only affordance
      // (it triggers `diagramChangedSelection`); there's nothing to deselect
      // headless.
      if (!diagram.headless) {
        const keysToRemove = new Set([linkData.key, ...linkData.labelKeys]);
        commitDiagramWhileSkippingUndoManager(
          diagram,
          (_, diagram) => removePartsFromSelection(keysToRemove)(diagram),
          DiagramTransaction.SET_DIAGRAM_SELECTION
        );
      }

      commitDiagramModel(
        diagram,
        (model) => {
          addDeletedData([linkData])(model);
          removeMapping(linkData)(model);
        },
        { layout: DiagramModelCommitLayout.PARTIAL }
      );
    },
    [diagram]
  );
};
